import { logger } from '@tunasong/models'
import { circleOfFifths as createCircleOfFifths, KEY_POOL } from '@tunasong/music-lib'
import type { Scale } from '@tunasong/schemas'
import { useDesignTokens } from '@tunasong/ui-lib'
import type { FC, MouseEvent } from 'react'
import React, { useEffect, useEffectEvent } from 'react'
import { hitTestCof } from './cof-hit-test.js'
import { drawCircleOfFifths } from './draw-circle.js'

export interface CofSelectedKey {
  name: string
  ring: 'major' | 'minor'
}

export interface CircleOfFifthsProps {
  className?: string
  /**
   * `null` is accepted at runtime — `MusicContextType.scale.current` is
   * declared as `Scale | undefined` in the type, but
   * `plugins/section/src/context.ts` and `editor.rootElement?.scale` can
   * resolve to `null` after a "Clear scale" action. Old code typed this
   * as `Scale | undefined`, which made `scale.root` throw on null inputs
   * (GH #1365). Accept null and treat as "no scale".
   */
  scale?: Scale | null
  /** CoF Wheel v1: highlight the active selected key. */
  selectedKey?: CofSelectedKey
  /** CoF Wheel v1: major-key names (or 'Xm' for minors) to glow as the closely-related cluster. */
  highlightedKeys?: readonly string[]
  /** CoF Wheel v1: fires when a wheel slot is clicked. */
  onKeySelect?: (key: CofSelectedKey) => void
}

const canvasStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  margin: 0,
  padding: 0,
}

const DEFAULT_SCALE = { root: 'C', type: 'major' } as Scale

export const CircleOfFifths: FC<CircleOfFifthsProps> = props => {
  const { className, selectedKey, highlightedKeys, onKeySelect } = props
  /**
   * `??` (not destructuring default) so we coalesce both `null` and
   * `undefined` to the default. JS destructuring defaults only fire for
   * `undefined`, which left `scale.root` throwing on null in production
   * (GH #1365).
   */
  const scale = props.scale ?? DEFAULT_SCALE
  const [canvas, setCanvas] = React.useState<HTMLCanvasElement | null>(null)
  const tokens = useDesignTokens()

  const draw = (canvas: HTMLCanvasElement, scale: Scale) => {
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      return
    }
    const PIXEL_RATIO = window.devicePixelRatio
    canvas.width = canvas.offsetWidth * PIXEL_RATIO
    /** height === width */
    canvas.height = canvas.width
    ctx.setTransform(PIXEL_RATIO, 0, 0, PIXEL_RATIO, 0, 0)

    logger.debug(`Redraw to ${canvas.width}:${canvas.height}, offset: ${canvas.offsetHeight}:${canvas.offsetWidth}`)
    const circleOfFifths = createCircleOfFifths(scale)
    drawCircleOfFifths({
      ctx,
      tokens,
      width: canvas.offsetWidth,
      height: canvas.offsetHeight,
      circleOfFifths,
      selectedKey,
      highlightedKeys,
    })
  }

  const drawEvent = useEffectEvent((canvas: HTMLCanvasElement | null, scale: Scale) => {
    if (!(canvas && scale)) {
      return
    }
    draw(canvas, scale)
  })

  useEffect(() => {
    drawEvent(canvas, scale)

    /** Redraw on resize */
    const redraw = () => drawEvent(canvas, scale)
    window.addEventListener('resize', redraw)
    return () => {
      /** Unsubscribe on unmount */
      window.removeEventListener('resize', redraw)
    }
  }, [scale, canvas, selectedKey, highlightedKeys])

  const handleClick = (event: MouseEvent<HTMLCanvasElement>): void => {
    if (!onKeySelect || !canvas) {
      return
    }
    const rect = canvas.getBoundingClientRect()
    const x = event.clientX - rect.left
    const y = event.clientY - rect.top
    const hit = hitTestCof(x, y, canvas.offsetWidth, canvas.offsetHeight)
    if (!hit) {
      return
    }
    const slot = KEY_POOL[hit.slotIndex]
    if (!slot) {
      return
    }
    const name = hit.ring === 'major' ? slot.canonical : slot.relativeMinor
    onKeySelect({ name, ring: hit.ring })
  }

  if (!(scale.root && scale.type)) {
    return null
  }

  return (
    <canvas
      style={canvasStyle}
      className={className}
      ref={setCanvas}
      width="300"
      height="300"
      onClick={onKeySelect ? handleClick : undefined}
    />
  )
}

export default CircleOfFifths
